import React, { useState, useEffect, useCallback } from 'react';
import styles from './CreateProgram.module.scss';
import TextField from '../../../components/textField/TextField';
import Dropdown from '../../../components/dropdown/Dropdown';
import Button from '../../../components/button/Button';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import CustomDatePicker from '../../../components/customDatePicker/CustomDatePicker';
import TextAreaField from '../../../components/textAreaField/TextAreaField';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import ProgramStudentDetails from './ProgramStudentDetails';
import ProgramClinicalSiteDetails from './ProgramClinicalSiteDetails';
import ProgramRotationDetails from './ProgramRotationDetails';
import Modal from '../../../components/modal/Modal';

const CreateProgram = () => {
    const navigate = useNavigate();
    const { showToast } = useToasts();
    const { role, can } = useAuth();
    const canEditPrograms = role !== 'Program Coordinator' || can('canEditPrograms');
    const { programId } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isValidated, setIsValidated] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [showScheduleWarning, setShowScheduleWarning] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        status: true,
        description: '',
        minimumRequiredScore: '',
        numberOfYears: '',
        startDate: null,
        endDate: null,
        students: [],
        clinicalSites: [],
        programYears: []
    });

    const fetchProgramDetails = useCallback(async () => {
        if (!programId) return;
        setIsLoading(true);
        try {
            const response = await axiosClient.get(`/programs/${programId}`);
            const data = response.data.data || response.data;
            const fetchedData = {
                title: data.title || '',
                status: data.status === 'Active' || data.status === true,
                description: data.description || '',
                minimumRequiredScore: data.minimumRequiredScore || '',
                numberOfYears: data.numberOfYears || '',
                startDate: data.startDate ? new Date(data.startDate) : null,
                endDate: data.endDate ? new Date(data.endDate) : null,
                students: data.students || [],
                clinicalSites: data.clinicalSites || [],
                programYears: data.programYears || []
            };
            setFormData(fetchedData);
            setInitialData(fetchedData);
        } catch (error) {
            showToast(error.message, 'error');
            navigate('/programs');
        } finally {
            setIsLoading(false);
        }
    }, [programId, showToast, navigate]);

    useEffect(() => {
        fetchProgramDetails();
    }, [fetchProgramDetails]);

    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!formData.title?.trim()) {
            newErrors.title = 'Program Title is required';
        }

        if (!formData.startDate) {
            newErrors.startDate = 'Start Date is required';
        }

        if (!formData.endDate) {
            newErrors.endDate = 'End Date is required';
        }

        if (formData.minimumRequiredScore === '' || formData.minimumRequiredScore === null || formData.minimumRequiredScore === undefined) {
             newErrors.minimumRequiredScore = 'Minimum Required Score is required';
        } else if (parseFloat(formData.minimumRequiredScore) < 1) {
             newErrors.minimumRequiredScore = 'Minimum value should be 1';
        } else if (parseFloat(formData.minimumRequiredScore) > 5) {
             newErrors.minimumRequiredScore = 'Maximum value should be 5';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length > 0 ? Object.keys(newErrors)[0] : null;
    }, [formData]);

    const scrollToFirstError = (field) => {
        const headerHeight = 72;
        const element = document.querySelector(`[data-error-field="${field}"]`);
        if (element) {
            const elementTop = element.offsetTop - headerHeight;
            window.scrollTo({
                top: elementTop,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        if (isValidated) {
            validateForm();
        }
    }, [formData, isValidated, validateForm]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const hasScheduleAffectingChanges = () => {
        if (!initialData) return false;
        return (
            formData.numberOfYears !== initialData.numberOfYears ||
            (formData.startDate?.getTime() || null) !== (initialData.startDate?.getTime() || null) ||
            (formData.endDate?.getTime() || null) !== (initialData.endDate?.getTime() || null)
        );
    };

    const submitProgram = async () => {
        setIsLoading(true);
        try {
            const formatDate = (date) => {
                if (!date) return null;
                const d = new Date(date);
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${d.getFullYear()}-${month}-${day}`;
            };

            const payload = {
                title: formData.title,
                description: formData.description,
                startDate: formatDate(formData.startDate),
                endDate: formatDate(formData.endDate),
                minimumRequiredScore: formData.minimumRequiredScore ? parseFloat(formData.minimumRequiredScore) : 0,
                numberOfYears: formData.numberOfYears || null,
                status: formData.status
            };

            if (programId) {
                await axiosClient.patch(`/programs/${programId}`, payload);
                showToast('Program updated successfully');
                await fetchProgramDetails();
            } else {
                const response = await axiosClient.post('/programs/create', payload);
                const newProgramId = response.data?.program?.id;
                showToast('Program created successfully');
                if (newProgramId) {
                    navigate(`/programs/edit/${newProgramId}`);
                } else {
                    navigate('/programs');
                }
            }
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const firstErrorField = validateForm();
        if (firstErrorField) {
            setIsValidated(true);
            scrollToFirstError(firstErrorField);
            return;
        }

        if (programId && hasScheduleAffectingChanges()) {
            setShowScheduleWarning(true);
            return;
        }

        await submitProgram();
    };

    const isFormDirty = () => {
        if (!initialData) return true; // if no initial data, assume dirty (i.e. creating new)
        
        return (
            formData.title !== initialData.title ||
            formData.description !== initialData.description ||
            formData.status !== initialData.status ||
            formData.minimumRequiredScore !== initialData.minimumRequiredScore ||
            formData.numberOfYears !== initialData.numberOfYears ||
            (formData.startDate?.getTime() || null) !== (initialData.startDate?.getTime() || null) ||
            (formData.endDate?.getTime() || null) !== (initialData.endDate?.getTime() || null)
        );
    };

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <div className={styles.breadcrumbs}>
                <Breadcrumbs
                    breadcrumbs={[
                        { label: 'Program Management', path: '/programs' },
                        { label: programId ? 'Edit Program' : 'Create Program' }
                    ]}
                />
            </div>

            <div className={styles.container}>
                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                    <h2>Program Details</h2>
                    
                    <div className={styles.row}>
                        <div className={styles.column50} data-error-field="title">
                            <TextField
                                label="Program Title *"
                                value={formData.title}
                                onChange={(val) => handleChange('title', val)}
                                placeholder="Enter program title"
                                error={errors.title}
                                disabled={!canEditPrograms}
                            />
                        </div>
                        <div className={styles.column50}>
                             <Dropdown
                               label="Status"
                               options={[
                                 { value: 'Active', label: 'Active' },
                                 { value: 'Inactive', label: 'Inactive' }
                               ]}
                               value={formData.status ? 'Active' : 'Inactive'}
                               onChange={(val) => handleChange('status', val === 'Active')}
                               disabled={!canEditPrograms}
                             />
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.column100}>
                             <TextAreaField
                                label="Description"
                                value={formData.description}
                                onChange={(val) => handleChange('description', val)}
                                placeholder="Enter program description"
                                disabled={!canEditPrograms}
                             />
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.column25} data-error-field="minimumRequiredScore">
                            <TextField
                                label="Minimum Required Score *"
                                value={formData.minimumRequiredScore}
                                onChange={(val) => handleChange('minimumRequiredScore', val)}
                                placeholder="Enter minimum required score"
                                type="number"
                                min={1}
                                max={5}
                                error={errors.minimumRequiredScore}
                                disabled={!canEditPrograms}
                            />
                        </div>
                        <div className={styles.column25}>
                            <Dropdown
                                label="Number of Years"
                                options={[
                                    { value: 2, label: '2 Years' },
                                    { value: 3, label: '3 Years' }
                                ]}
                                value={formData.numberOfYears}
                                onChange={(val) => handleChange('numberOfYears', val)}
                                disabled={!canEditPrograms}
                            />
                        </div>
                        <div className={styles.column25} data-error-field="startDate">
                             <CustomDatePicker
                                label="Start Date *"
                                date={formData.startDate}
                                onChange={(date) => handleChange('startDate', date)}
                                showIcon={true}
                                maxDate={formData.endDate}
                                error={errors.startDate}
                                disabled={!canEditPrograms}
                             />
                        </div>
                        <div className={styles.column25} data-error-field="endDate">
                             <CustomDatePicker
                                label="End Date *"
                                date={formData.endDate}
                                onChange={(date) => handleChange('endDate', date)}
                                showIcon={true}
                                minDate={formData.startDate}
                                error={errors.endDate}
                                disabled={!canEditPrograms}
                             />
                        </div>
                    </div>

                    {canEditPrograms && (!programId || isFormDirty()) && (
                        <div className={styles.actions}>
                            <Button 
                                label="Cancel" 
                                variant="white" 
                                size="medium"
                                onClick={() => {
                                    if (programId && initialData) {
                                        setFormData(initialData);
                                        setErrors({});
                                    } else {
                                        navigate('/programs');
                                    }
                                }}
                            />
                            <Button 
                                label={programId ? 'Save Program' : 'Create Program'}
                                variant="primary" 
                                size="medium"
                                type="submit"
                            />
                        </div>
                    )}
                </form>
                {programId && (
                    <>
                        <ProgramStudentDetails 
                            students={formData.students} 
                            programId={programId} 
                            programTitle={formData.title}
                            onRefresh={fetchProgramDetails}
                        />
                        <ProgramClinicalSiteDetails 
                            clinicalSites={formData.clinicalSites} 
                            programId={programId} 
                            programTitle={formData.title}
                            onRefresh={fetchProgramDetails}
                        />
                        <ProgramRotationDetails
                            programYears={formData.programYears}
                            programId={programId}
                            programStartDate={formData.startDate}
                            programEndDate={formData.endDate}
                            onRefresh={fetchProgramDetails}
                        />
                    </>
                )}
            </div>
            {showScheduleWarning && (
                <Modal scrollable={false}>
                    <div className={styles.warningModal}>
                        <h2 className={styles.warningTitle}>Are you sure you want to save these changes?</h2>
                        <p className={styles.warningMessage}>
                            Changing the Number of Years, Start Date, or End Date may delete existing schedules associated with this program.
                        </p>
                        <div className={styles.warningActions}>
                            <Button
                                label="Confirm"
                                size="small"
                                variant="secondary"
                                onClick={async () => {
                                    setShowScheduleWarning(false);
                                    await submitProgram();
                                }}
                            />
                            <Button
                                label="Cancel"
                                size="small"
                                variant="cancel"
                                onClick={() => setShowScheduleWarning(false)}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default CreateProgram;
